import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sort as SortType } from "/app/src/types";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  ColumnDef,
  ColumnSort,
} from "@tanstack/react-table";
import Pagination from "./pagination";
import {
  Table as HeroUITable,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Spinner,
} from "@heroui/react";

/**
 * Empty table component
 * @param emptyText
 */
export function TableEmpty({ emptyText }: { emptyText: string }) {
  return (
    <div className="reportLoading text-center">
      {emptyText && <span className="loadingText">{emptyText}</span>}
    </div>
  );
}

/**
 * Table component that uses React-Table v8
 * @param rows
 * @param tableColumns
 * @param length
 * @param sort
 * @param setSort
 * @param rowClicked
 * @param emptyText
 * @param enableRowSelection
 * @param paginationEnabled
 * @param loading
 * @param classStyle
 * @returns React-Table v8 table
 */
export default function Table({
  rows,
  tableColumns,
  length,
  sort,
  setSort,
  rowClicked,
  emptyText,
  enableRowSelection = false,
  paginationEnabled,
  loading = false,
  setSelectedRows,
  clearSelectedRows = false,
  selectionMode = "none",
  disabledKeys = new Set(),
}: {
  rows: any[];
  tableColumns: ColumnDef<any>[];
  length: number;
  sort: ColumnSort[];
  setSort: React.Dispatch<React.SetStateAction<SortType>>;
  rowClicked?: (key: number | string | undefined) => void;
  emptyText: string;
  loading?: boolean;
  enableRowSelection?: boolean | ((row) => boolean);
  paginationEnabled?: {
    setPageSize: (pageSize: number) => void;
    setPage: (page: number) => void;
    currentPage: number;
    pageSize: number;
  };
  setSelectedRows?: (rows?: Record<string, boolean>) => void;
  clearSelectedRows?: boolean;
  selectionMode?: "none" | "single" | "multiple";
  disabledKeys?: Set<string>;
}) {
  const data = useMemo(() => rows, [rows]);
  const columns = useMemo(() => tableColumns, [tableColumns]);
  const { t } = useTranslation();
  const hover = rowClicked ? true : false;
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedRow, setSelectedRow] = useState<number | string | undefined>(
    undefined,
  );
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting: sort,
      rowSelection,
    },
    onSortingChange: setSort,
    onRowSelectionChange: setRowSelection,
    enableSorting: !loading,
    manualSorting: true,
    enableRowSelection,
    enableColumnResizing: true,
  });

  useEffect(() => {
    if (setSelectedRows) {
      // skipcq: JS-W1038
      setSelectedRows(rowSelection);
    }
  }, [rowSelection, setSelectedRows, table]);

  //meta tag is used to determine if the column is clickable
  const onSubmitHandler = useCallback(
    (rowId: number | string | undefined, cell) => () => {
      // If user is selecting text, don't trigger row click
      const selection = window.getSelection();
      if (selection && selection.toString().trim().length > 0) {
        return;
      }

      if (cell?.column?.columnDef?.meta?.clickable === false) {
        return;
      }
      const parsed =
        rowId !== undefined && rowId !== null ? Number(rowId) : undefined;
      const id =
        parsed !== undefined && !Number.isNaN(parsed)
          ? parsed
          : typeof rowId === "string"
            ? rowId
            : undefined;
      rowClicked?.(id);
      setSelectedRow(id);
    },
    [rowClicked],
  );

  // when the clear selected rows prop is changed, clear the selected rows
  useEffect(() => {
    setRowSelection({});
    setSelectedKeys(new Set());
  }, [clearSelectedRows, setRowSelection]);

  // Handle HeroUI multi-selection changes
  const handleMultiSelectionChange = useCallback(
    (keys: Set<React.Key> | "all") => {
      if (keys === "all") {
        // Select all non-disabled rows
        const allKeys = new Set<string>(
          rows
            .filter((row) => !disabledKeys.has(String(row.id)))
            .map((row) => String(row.id)),
        );
        setSelectedKeys(allKeys);
        if (setSelectedRows) {
          const newSelection: Record<string, boolean> = {};
          rows.forEach((row, index) => {
            if (allKeys.has(String(row.id))) {
              newSelection[index] = true;
            }
          });
          setSelectedRows(newSelection);
        }
      } else {
        setSelectedKeys(new Set(Array.from(keys).map(String)));
        if (setSelectedRows) {
          const newSelection: Record<string, boolean> = {};
          rows.forEach((row, index) => {
            if (keys.has(String(row.id))) {
              newSelection[index] = true;
            }
          });
          setSelectedRows(newSelection);
        }
      }
    },
    [rows, disabledKeys, setSelectedRows],
  );

  // Prepare HeroUI header columns and a map from columnKey -> react-table header
  const headerGroups = table.getHeaderGroups();
  // Use the react-table column id (header.column.id) as the stable column key
  const heroColumns = useMemo(
    () =>
      headerGroups.flatMap((hg) =>
        hg.headers.map((h) => ({ key: h.column.id })),
      ),
    [headerGroups],
  );
  const headerMap = useMemo(() => {
    const headerById: Record<string, any> = {};
    headerGroups.forEach((headerGroup) =>
      headerGroup.headers.forEach(
        (header) => (headerById[header.column.id] = header),
      ),
    );
    return headerById;
  }, [headerGroups]);

  const handleSelectionChange = useCallback(
    (keys: Set<React.Key> | "all") => {
      const key = Array.from(keys ?? new Set<React.Key>())[0];
      const parsed =
        key !== undefined && key !== null ? Number(key) : undefined;
      const id =
        parsed !== undefined && !Number.isNaN(parsed)
          ? parsed
          : typeof key === "string"
            ? key
            : undefined;
      setSelectedRow(id);
    },
    [setSelectedRow],
  );

  /**
   * Renders a skeleton table UI during loading or when there's no data.
   * @param {{ message?: string; isLoading?: boolean }} props - The skeleton display options.
   * @param {string} [props.message] - Optional message to display when no data is available.
   * @param {boolean} [props.isLoading] - Flag to show loading spinner.
   * @returns {JSX.Element} The skeleton table component.
   */
  const HeroTableSkeleton = ({
    message,
    isLoading,
  }: {
    message?: string;
    isLoading?: boolean;
  }) => (
    <HeroUITable aria-label="Data table">
      <TableHeader columns={heroColumns}>
        {(col) => {
          const header = headerMap[col.key];
          if (!header) return null;
          return (
            <TableColumn
              key={col.key}
              onClick={
                header.column?.columnDef?.meta?.sortable !== false
                  ? header.column.getToggleSortingHandler()
                  : undefined
              }
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {{ asc: "🔼", desc: "🔽" }[
                header.column.getIsSorted() as string
              ] ?? null}
            </TableColumn>
          );
        }}
      </TableHeader>
      <TableBody
        emptyContent={
          message ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {message}
            </div>
          ) : undefined
        }
        loadingContent={isLoading ? <Spinner size="lg" /> : undefined}
        isLoading={Boolean(isLoading)}
      >
        {[]}
      </TableBody>
    </HeroUITable>
  );

  return (
    <>
      {length === null || loading ? (
        <HeroTableSkeleton isLoading />
      ) : tableColumns.length === 0 ? (
        <HeroTableSkeleton message={t("translation:no_columns_selected")} />
      ) : length === 0 ? (
        <HeroTableSkeleton message={emptyText} />
      ) : (
        <HeroUITable
          aria-label={t("translation:table")}
          selectionMode={selectionMode !== "none" ? selectionMode : undefined}
          selectedKeys={
            selectionMode === "multiple"
              ? selectedKeys
              : selectedRow !== undefined
                ? new Set([String(selectedRow)])
                : new Set()
          }
          onSelectionChange={
            selectionMode === "multiple"
              ? handleMultiSelectionChange
              : handleSelectionChange
          }
          disabledKeys={disabledKeys}
        >
          <TableHeader columns={heroColumns}>
            {(col) => {
              const header = headerMap[col.key];
              if (!header) return null;
              return (
                <TableColumn
                  key={col.key}
                  onClick={
                    header.column?.columnDef?.meta?.sortable !== false
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {{ asc: "🔼", desc: "🔽" }[
                    header.column.getIsSorted() as string
                  ] ?? null}
                </TableColumn>
              );
            }}
          </TableHeader>

          <TableBody items={table.getRowModel().rows}>
            {(row: any) => (
              <TableRow
                key={row.original.id}
                className={`${hover ? "cursor-pointer hover:bg-[#F7F7F7]" : ""}                }`}
              >
                {(columnKey: string) => {
                  // Find the react-table cell that corresponds to the HeroUI column key
                  const cell = row
                    .getVisibleCells()
                    .find((c) => c.column.id === columnKey);
                  if (!cell) return <TableCell>{null}</TableCell>;
                  return (
                    <TableCell
                      key={cell.id}
                      onClick={onSubmitHandler(row.original.id, cell)}
                    >
                      <div className="inline-block w-full">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  );
                }}
              </TableRow>
            )}
          </TableBody>
        </HeroUITable>
      )}
      {paginationEnabled && (
        <Pagination
          length={length}
          pageSize={paginationEnabled.pageSize}
          setPageSize={paginationEnabled.setPageSize}
          currentPage={paginationEnabled.currentPage}
          setPage={paginationEnabled.setPage}
        />
      )}
    </>
  );
}
